import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Modal,
  Button,
  IconButton,
  Typography,
  Card,
  Grid,
  Fab,
  Tooltip,
} from "@mui/material";
import { Close, Bookmark, Share, MenuBook } from "@mui/icons-material";
import ReactPlayer from "react-player";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import UnlockScreen from "./UnlockScreen";
const VideoPlayerModalHotstar = ({
  open,
  handleClose,
  selectedVideo,
  selectedEpisodes,
  setSelectedVideo,
}) => {
  const [activeRange, setActiveRange] = useState([0, 24]); // Default: Load episodes 1-25

  // Define episode ranges
  const episodeRanges = [
    [0, 24],  // 1-25
    [25, 49], // 26-50
    [50, 74], // 51-75
    [75, 99], // 76-100
  ];

  // Filter episodes based on active range
  const displayedEpisodes = selectedEpisodes.slice(activeRange[0], activeRange[1] + 1);

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const swiperRef = useRef(null);
  const playerRef = useRef(null);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [currentIndex, setCurrentIndex] = useState(
    selectedEpisodes.findIndex((ep) => ep.video === selectedVideo)
  );
  const [showEpisodes, setShowEpisodes] = useState(false);
  console.log("VideoPlayerModalHotstar:", selectedEpisodes.length, selectedEpisodes);
  useEffect(() => {
    if (selectedVideo) {
      const newIndex = selectedEpisodes.findIndex((ep) => ep.video === selectedVideo);
      if (newIndex !== -1 && newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
        setTimeout(() => {
          if (swiperRef.current) {
            swiperRef.current.slideTo(newIndex); // Ensure Swiper slides to the new index
          }
        }, 100);
      }
    }
  }, [selectedVideo]);

  // Auto-play next episode when current video ends
  const handleEnd = () => {
    if (currentIndex < selectedEpisodes.length - 1) {
      const nextIndex = currentIndex + 1;
      setSelectedVideo(selectedEpisodes[nextIndex].video);
      setCurrentIndex(nextIndex);
      swiperRef.current?.slideTo(nextIndex);
    }
  };
  const handleEpisodeSelect = (video, locked) => {
    if (locked) {
      setUnlockModalOpen(true);
    } else {
      const newIndex = selectedEpisodes.findIndex((ep) => ep.video === video);
      if (newIndex !== -1) {
        setSelectedVideo(video);
        setCurrentIndex(newIndex);
        setTimeout(() => {
          if (swiperRef.current) {
            swiperRef.current.slideTo(newIndex);
          }
        }, 100);
      }
      setShowEpisodes(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          backgroundColor: "#000",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: "16px", right: "16px", color: "#fff", zIndex: 10 }}
        >
          <Close />
        </IconButton>

        {/* Video Title */}
        <Typography
          sx={{
            color: "#fff",
            fontSize: "18px",
            fontWeight: "bold",
            marginTop: "16px",
            textAlign: "center",
          }}
        >
          {selectedEpisodes[currentIndex]?.episodeTitle || "Selected Video"}
        </Typography>

        {/* Video Player with Swiper */}
        <Box sx={{ width: "100%", height: "100vh", overflow: "hidden" }}>
          <Swiper
            ref={swiperRef}
            direction="vertical"
            modules={[Mousewheel, Pagination]}
            spaceBetween={10}
            slidesPerView={1}
            loop={false}
            mousewheel={{ forceToAxis: true, sensitivity: 1 }}
            pagination={{ clickable: false, el: null }}
            initialSlide={currentIndex} // Ensure Swiper starts at the correct index
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
          >
            {selectedEpisodes.slice(0, 8).map((video, idx) => (
              <SwiperSlide key={video.video}>
                <ReactPlayer
                  key={video.video} // Forces re-render when video changes
                  ref={currentIndex === idx ? playerRef : null} // Attach ref only to active player
                  url={video.video}
                  playing={currentIndex === idx} // Play only when active
                  controls
                  width="100%"
                  height="90%"
                  muted={currentIndex !== idx} // Mute when not active
                  onEnded={handleEnd}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        {/* Floating Action Buttons */}

        <Box
          sx={{
            position: "absolute",
            bottom: "140px",
            right: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          {/* Bookmark FAB */}
          <Tooltip title="Bookmark" placement="left">
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Fab
                color="secondary"
                sx={{
                  backgroundColor: "#FFB300",
                  "&:hover": { backgroundColor: "#E69C00" },
                }}
              >
                <Bookmark />
              </Fab>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "bold",
                  textShadow: "0px 2px 4px rgba(0,0,0,0.5)", // Improves text visibility
                  background: "rgba(0, 0, 0, 0.5)", // Adds background to avoid blending issues
                  padding: "4px 8px",
                  borderRadius: "8px",
                }}
              >
                11.1K
              </Typography>
            </Box>
          </Tooltip>

          {/* Share FAB */}
          <Tooltip title="Share" placement="left">
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Fab
                color="primary"
                sx={{
                  backgroundColor: "#4CAF50",
                  "&:hover": { backgroundColor: "#388E3C" },
                }}
              >
                <Share />
              </Fab>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "bold",
                  textShadow: "0px 2px 4px rgba(0,0,0,0.5)",
                  background: "rgba(0, 0, 0, 0.5)",
                  padding: "4px 8px",
                  borderRadius: "8px",
                }}
              >
                Share
              </Typography>
            </Box>
          </Tooltip>

          {/* Show Episodes FAB */}
          <Tooltip title="Show Episodes" placement="left">
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Fab
                color="primary"
                onClick={() => setShowEpisodes(true)}
                sx={{
                  backgroundColor: "#FF4500",
                  "&:hover": { backgroundColor: "#e63900" },
                }}
              >
                <MenuBook />
              </Fab>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "bold",
                  textShadow: "0px 2px 4px rgba(0,0,0,0.5)",
                  background: "rgba(0, 0, 0, 0.5)",
                  padding: "4px 8px",
                  borderRadius: "8px",
                }}
              >
                Episodes
              </Typography>
            </Box>
          </Tooltip>
        </Box>

       {/* Show Episodes Modal */}
{showEpisodes && (
  <Modal
    open={showEpisodes}
    onClose={() => setShowEpisodes(false)}
    sx={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        width: "100%",
        maxWidth: "500px",
        backgroundColor: "#111",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",
        borderTop: "1px solid #333",
        padding: "16px",
        boxShadow: "0px -4px 10px rgba(0,0,0,0.5)",
      }}
    >
      {/* Modal Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "10px",
        }}
      >
        {/* Title - Aligned Left */}
        <Typography
          variant="h6"
          sx={{
            color: "#fff",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          All {selectedEpisodes.length} Episodes
        </Typography>

        {/* Close Button (Restored) */}
        <Typography
          sx={{
            color: "#FF4500",
            fontWeight: "bold",
            fontSize: "14px",
            cursor: "pointer",
          }}
          onClick={() => setShowEpisodes(false)}
        >
          Close
        </Typography>
      </Box>

      {/* Range Labels (Aligned Left) */}
      <Box sx={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        {episodeRanges.map((range, index) => (
          <Typography
            key={index}
            sx={{
              cursor: "pointer",
              color: activeRange[0] === range[0] ? "#FF4500" : "#fff",
              fontSize: "14px",
              fontWeight: activeRange[0] === range[0] ? "bold" : "normal",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #444",
              backgroundColor: activeRange[0] === range[0] ? "#222" : "transparent",
            }}
            onClick={() => setActiveRange(range)}
          >
            {range[0] + 1}-{range[1] + 1}
          </Typography>
        ))}
      </Box>

      {/* Episodes Grid */}
      <Grid
        container
        spacing={2}
        sx={{
          maxHeight: "50vh",
          overflowY: "auto",
          paddingBottom: "16px",
        }}
      >
        {displayedEpisodes.map((ep, index) => (
          <Grid item xs={3} sm={2} md={1} key={index}>
            <Card
              onClick={() => {
                if (ep.locked) {
                  setUnlockModalOpen(true);
                } else {
                  handleEpisodeSelect(ep.video, false);
                }
              }}
              sx={{
                backgroundColor: ep.isVideoWatched ? "#FF4500" : "#222",
                textAlign: "center",
                cursor: ep.locked ? "not-allowed" : "pointer",
                borderRadius: "8px",
                padding: "12px",
                position: "relative",
              }}
            >
              {/* Show only episode number */}
              <Typography
                sx={{
                  color: ep.locked ? "#888" : "#fff",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                {activeRange[0] + index + 1} {/* Correct numbering */}
              </Typography>
              {ep.locked && (
                <LockIcon
                  sx={{
                    color: "#888",
                    fontSize: "18px",
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                  }}
                />
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Modal>
)}

        {/* Unlock Modal */}
        <Modal open={unlockModalOpen} onClose={() => setUnlockModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#1c1c1e",
              padding: "16px",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "400px",
              color: "#fff",
              boxShadow: 24,
            }}
          >
            <UnlockScreen />
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
};

export default VideoPlayerModalHotstar;